import { Link } from 'react-router-dom'
import { FiAlertTriangle, FiTrendingUp } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'marco-legal', label: 'Marco legal: dónde se regulan las sanciones' },
  { id: 'cuantias-2026', label: 'Cuantías actualizadas en 2026' },
  { id: 'agravantes', label: 'Agravantes que disparan la multa' },
  { id: 'casos-reales', label: 'Casos reales de la ITSS (2023-2025)' },
  { id: 'efecto-domino', label: 'El efecto dominó: lo que sale más caro que la multa' },
  { id: 'prevencion', label: 'Cómo blindar tu empresa' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿Cuál es la multa máxima por no fichar en 2026?', answer: 'En grado máximo, una infracción grave puede alcanzar 7.500 € por centro de trabajo. Si concurren agravantes (reincidencia, número de trabajadores afectados, ocultación), la suma puede multiplicarse hasta 187.515 € sumando todos los empleados implicados.' },
  { question: '¿La multa es por empresa o por trabajador?', answer: 'La sanción base es por infracción, pero al graduarla la ITSS computa el número de trabajadores afectados como criterio agravante. En la práctica, una empresa con 30 empleados sin registro horario puede recibir sanciones que se calculan tomando como referencia el conjunto.' },
  { question: '¿Pueden sancionarme solo por no tener pausas registradas?', answer: 'La obligación legal mínima son las horas de inicio y fin. No registrar las pausas no es sancionable per se, pero genera presunción a favor del trabajador en reclamaciones de horas extra y dificulta la defensa de la empresa.' },
  { question: '¿Qué pasa si firmo un acta de conformidad con el inspector?', answer: 'Si reconoces los hechos y pagas en plazo, te aplican una reducción del 50 % (acta con avenencia) o del 40 % por pronto pago. Es la vía más rápida cuando la infracción es clara.' },
  { question: '¿Una asesoría puede ser responsable solidaria?', answer: 'No. La obligación recae sobre la empresa empleadora. La asesoría puede tener responsabilidad contractual frente a su cliente si su asesoramiento fue negligente, pero la sanción administrativa la asume la empresa.' },
]

export default function SancionesNoFichar() {
  return (
    <BlogLayout slug="sanciones-no-fichar-2026" toc={toc} faqs={faqs}>
      <section id="marco-legal" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Marco legal: dónde se regulan las sanciones</h2>
        <p>
          Las multas por incumplir el registro horario no están en el Estatuto de los Trabajadores, sino en la <strong>Ley sobre Infracciones y Sanciones en el Orden Social</strong> (LISOS, Real Decreto Legislativo 5/2000). Concretamente, el <strong>artículo 7.5</strong> tipifica como <strong>infracción grave</strong> "la transgresión de las normas y los límites legales o pactados en materia de jornada, trabajo nocturno, horas extraordinarias, horas complementarias, descansos, vacaciones, permisos, registro de jornada y, en general, el tiempo de trabajo".
        </p>
        <p>
          El artículo 40 fija las cuantías por tramos (mínimo, medio y máximo) y permite a la inspección graduarlas en función de varios factores que veremos más adelante.
        </p>
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg my-6">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 mb-1">Importante</p>
              <p className="text-amber-700 text-sm">No registrar la jornada no es la única vía sancionable. Si los registros son <strong>falsos, inexactos o no se conservan 4 años</strong>, también caes en el mismo artículo 7.5.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="cuantias-2026" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Cuantías actualizadas en 2026</h2>
        <p>
          Las cifras que figuran a continuación corresponden a la última actualización de la LISOS aplicable en 2026. Cada infracción grave puede sancionarse en uno de tres grados según las circunstancias del caso:
        </p>
        <div className="overflow-x-auto my-6">
          <table className="min-w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-semibold text-gray-900">Grado</th>
                <th className="text-left p-3 font-semibold text-gray-900">Mínima</th>
                <th className="text-left p-3 font-semibold text-gray-900">Máxima</th>
                <th className="text-left p-3 font-semibold text-gray-900">Cuándo se aplica</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="p-3 font-medium">Grado mínimo</td><td className="p-3">751 €</td><td className="p-3">1.500 €</td><td className="p-3 text-gray-500">Empresa de buena fe, primera infracción, escasos trabajadores afectados</td></tr>
              <tr><td className="p-3 font-medium">Grado medio</td><td className="p-3">1.501 €</td><td className="p-3">3.750 €</td><td className="p-3 text-gray-500">Negligencia clara, varios empleados afectados</td></tr>
              <tr><td className="p-3 font-medium">Grado máximo</td><td className="p-3">3.751 €</td><td className="p-3">7.500 €</td><td className="p-3 text-gray-500">Mala fe, reincidencia, ocultación o muchos trabajadores</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          Cuando la infracción se considera <strong>muy grave</strong> (concurrencia con vulneración de derechos fundamentales, fraude para evitar el pago de horas extra) las cuantías saltan a un nuevo escalado que puede llegar a los <strong>187.515 €</strong> sumando varios trabajadores.
        </p>
      </section>

      <section id="agravantes" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Agravantes que disparan la multa</h2>
        <p>El art. 39 LISOS lista los criterios que la inspección utiliza para graduar:</p>
        <ul className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Número de trabajadores afectados.</strong> Una empresa con 4 empleados sin fichar pagará menos que una con 40, aunque el incumplimiento sea idéntico.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Reincidencia.</strong> Comisión de una infracción de la misma naturaleza en los 365 días anteriores. La sanción se incrementa entre un 50 % y el doble.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Negligencia o intencionalidad.</strong> No es lo mismo "no sabía que tenía que registrar" que "tenía registros pero los ocultó al inspector".</div>
          </li>
          <li className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Cifra de negocio y permanencia en la falta.</strong> Empresas con mayor capacidad económica reciben sanciones proporcionalmente más altas.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Concurrencia con falta de pago de horas extra.</strong> Si los registros falsean horas trabajadas para no abonar extras, la infracción se reconduce a muy grave.</div>
          </li>
        </ul>
      </section>

      <section id="casos-reales" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Casos reales de la ITSS (2023-2025)</h2>
        <p>
          La Inspección de Trabajo publica memorias anuales y resoluciones administrativas que ilustran cómo se aplican las cuantías:
        </p>
        <div className="space-y-4 my-6">
          <div className="border-l-4 border-blue-400 pl-4 py-2">
            <p className="font-semibold text-gray-900 mb-1">Restauración (12 empleados, Cataluña, 2024)</p>
            <p className="text-sm text-gray-600">Sin registro horario y con sospecha de horas extra impagadas. Sanción: <strong>6.250 €</strong> + reclamación retroactiva de 14.300 € en horas no pagadas y cotizaciones.</p>
          </div>
          <div className="border-l-4 border-blue-400 pl-4 py-2">
            <p className="font-semibold text-gray-900 mb-1">Comercio minorista (3 empleados, Madrid, 2025)</p>
            <p className="text-sm text-gray-600">Hojas de papel con incoherencias entre fechas. Considerado grado mínimo: <strong>1.200 €</strong>.</p>
          </div>
          <div className="border-l-4 border-blue-400 pl-4 py-2">
            <p className="font-semibold text-gray-900 mb-1">Empresa de limpieza (54 empleados, Andalucía, 2024)</p>
            <p className="text-sm text-gray-600">Reincidente, sin registros y con <Link to="/blog/inspeccion-trabajo-control-horario">denuncia de plantilla que disparó la actuación de la ITSS</Link>. Grado máximo + agravantes: <strong>23.400 €</strong>.</p>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Las cantidades exactas dependen del expediente. Estos importes son aproximados a partir de resoluciones públicas.
        </p>
      </section>

      <section id="efecto-domino" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">El efecto dominó: lo que sale más caro que la multa</h2>
        <p>
          La sanción administrativa rara vez es el coste mayor. Lo que verdaderamente arruina una pyme es lo que viene después:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Reclamaciones de horas extra</h3>
            <p className="text-sm text-gray-600">Sin registro, la jurisprudencia presume que el empleado dice la verdad. Horas extras de 4 años retroactivos × varios empleados = decenas de miles de euros.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Cotizaciones a la Seguridad Social</h3>
            <p className="text-sm text-gray-600">La TGSS reclama las cotizaciones no abonadas con recargos. Suele aparecer en la misma actuación inspectora.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Pérdida de bonificaciones</h3>
            <p className="text-sm text-gray-600">Las empresas sancionadas por infracciones graves pierden bonificaciones a la contratación durante 1 a 2 años.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Reputación e imagen</h3>
            <p className="text-sm text-gray-600">Las sanciones graves figuran en registros públicos. En licitaciones públicas y rondas de inversión penalizan.</p>
          </div>
        </div>
      </section>

      <section id="prevencion" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Cómo blindar tu empresa</h2>
        <ol className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <div><strong>Implanta un sistema digital con trazabilidad.</strong> Papel y Excel se manipulan; un sistema digital con auditoría inmutable y exportación PDF firmada blinda la prueba, especialmente con vistas al <Link to="/blog/real-decreto-2026-fichaje-digital">nuevo Real Decreto de fichaje digital previsto para 2026</Link>.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <div><strong>Conserva los registros 4 años.</strong> No basta con tenerlos: deben ser accesibles a Inspección, comité de empresa y trabajadores afectados.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <div><strong>Negocia el método con la representación legal.</strong> El art. 34.9 ET exige consulta. Documenta el acuerdo o, en su defecto, la consulta.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
            <div><strong>Audita periódicamente.</strong> Una vez al trimestre, exporta los registros y comprueba huecos, fichajes fuera de horario y descuadres con calendario.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
            <div><strong>Forma a los mandos intermedios.</strong> El 60 % de los incumplimientos vienen de jefes de equipo que "olvidan" pedir el fichaje.</div>
          </li>
        </ol>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        Si quieres profundizar en cómo funciona el registro de jornada, te recomendamos nuestra
        <Link to="/blog/registro-horario-obligatorio-espana" className="text-blue-600 hover:underline mx-1">guía completa del registro horario obligatorio</Link>
        y la página
        <Link to="/control-horario-empresas" className="text-blue-600 hover:underline mx-1">control horario para empresas</Link>.
      </div>
    </BlogLayout>
  )
}
